import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Paperclip, Download } from 'lucide-react';
import { uploadCertificate, openCertificate } from './AbsenceSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const TYPE_STYLE = {
  ferie: { label: '🌴 Ferie', cell: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  permesso: { label: '🕐 Permesso', cell: 'bg-sky-500', badge: 'bg-sky-100 text-sky-700' },
  malattia: { label: '🤒 Malattia', cell: 'bg-rose-500', badge: 'bg-rose-100 text-rose-700' },
};
const fmtD = (d) => new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });

const easterMonday = (y) => {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const mm = Math.floor((a + 11 * h + 22 * l) / 451);
  const em = new Date(y, Math.floor((h + l - 7 * mm + 114) / 31) - 1, ((h + l - 7 * mm + 114) % 31) + 2);
  return `${em.getFullYear()}-${String(em.getMonth() + 1).padStart(2, '0')}-${String(em.getDate()).padStart(2, '0')}`;
};

const italianHolidays = (y) => {
  const map = {
    [`${y}-01-01`]: 'Capodanno', [`${y}-01-06`]: 'Epifania', [`${y}-04-25`]: 'Liberazione',
    [`${y}-05-01`]: 'Festa del Lavoro', [`${y}-06-02`]: 'Festa della Repubblica',
    [`${y}-08-15`]: 'Ferragosto', [`${y}-11-01`]: 'Ognissanti', [`${y}-12-08`]: 'Immacolata',
    [`${y}-12-25`]: 'Natale', [`${y}-12-26`]: 'Santo Stefano',
  };
  map[easterMonday(y)] = "Lunedì dell'Angelo";
  return map;
};

const DAY_KEYS_JS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const fmtHours = (mins) => (mins % 60 === 0 ? `${mins / 60}` : (mins / 60).toFixed(1).replace('.', ','));

const scheduledMinutes = (emp, wd) => {
  // ore previste dall'orario settimanale del dipendente (es. 9-13 + 14-18 = 480 min = 8)
  const ranges = (emp.weekly_schedule || {})[DAY_KEYS_JS[wd]] || [];
  let tot = 0;
  ranges.forEach(r => {
    try {
      const [h1, m1] = String(r[0]).split(':').map(Number);
      const [h2, m2] = String(r[1]).split(':').map(Number);
      tot += (h2 * 60 + m2) - (h1 * 60 + m1);
    } catch (_) { /* fascia malformata */ }
  });
  return Math.max(0, tot);
};

const emptyDialog = () => ({
  employee_id: '', type: 'ferie', date_from: new Date().toLocaleDateString('sv-SE'),
  date_to: new Date().toLocaleDateString('sv-SE'), time_from: '09:00', time_to: '13:00', note: '', force: false,
});

export const AdminAbsencesTab = ({ emps }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [absences, setAbsences] = useState([]);
  const [hoursMap, setHoursMap] = useState({});
  const [manualMap, setManualMap] = useState({});
  const [dayDialog, setDayDialog] = useState(null);
  const [savingDay, setSavingDay] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [certDialog, setCertDialog] = useState(null);
  const [certFiles, setCertFiles] = useState([]);
  const [certProt, setCertProt] = useState('');
  const [certBusy, setCertBusy] = useState(false);

  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const first = `${month}-01`;
  const last = `${month}-${String(lastDay).padStart(2, '0')}`;

  const load = useCallback(async () => {
    try {
      const [ra, rt] = await Promise.all([
        axios.get(`${API}/hr/absences?date_from=${first}&date_to=${last}`, { headers: authHeaders() }),
        axios.get(`${API}/hr/attendance?date_from=${first}&date_to=${last}`, { headers: authHeaders() }),
      ]);
      setAbsences(ra.data.absences || []);
      const hm = {}, mm = {};
      (rt.data.rows || []).forEach(r => {
        const k = `${r.employee_id}_${r.day}`;
        if (r.worked_minutes > 0) hm[k] = r.worked_minutes;
        if (r.manual) mm[k] = true;
      });
      setHoursMap(hm);
      setManualMap(mm);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore caricamento assenze'); }
  }, [first, last]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!dialog.employee_id) { setError('Seleziona il dipendente'); return; }
    setSaving(true); setError('');
    try {
      const payload = { employee_id: dialog.employee_id, type: dialog.type, date_from: dialog.date_from, note: dialog.note, force: dialog.force };
      if (dialog.type === 'permesso') Object.assign(payload, { date_to: dialog.date_from, time_from: dialog.time_from, time_to: dialog.time_to });
      else payload.date_to = dialog.date_to;
      await axios.post(`${API}/hr/absences`, payload, { headers: authHeaders() });
      toast.success('Assenza registrata');
      setDialog(null); load();
    } catch (e) { setError(e?.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const cancel = async (a) => {
    if (!window.confirm(`Annullare l'assenza di ${a.employee_name}? I saldi verranno restituiti.`)) return;
    try {
      await axios.delete(`${API}/hr/absences/${a.id}`, { headers: authHeaders() });
      toast.success('Assenza annullata, saldi restituiti');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const activeEmps = emps.filter(e => e.active !== false);
  const days = Array.from({ length: lastDay }, (_, i) => i + 1);
  const holidayMap = italianHolidays(y);
  const absAt = (empId, day) => {
    const d = `${month}-${String(day).padStart(2, '0')}`;
    return absences.find(a => a.employee_id === empId && a.date_from <= d && a.date_to >= d);
  };

  const cellInfo = (e, d) => {
    const wd = new Date(y, m - 1, d).getDay();
    const iso = `${month}-${String(d).padStart(2, '0')}`;
    const hol = holidayMap[iso];
    const we = wd === 0 || wd === 6 || !!hol;
    const a = absAt(e.id, d);
    const worked = hoursMap[`${e.id}_${iso}`];
    const manual = manualMap[`${e.id}_${iso}`];
    if (we) return { cls: 'bg-slate-300', content: '', title: hol || '', mins: 0, iso, editable: false };
    if (a) {
      const letter = { ferie: 'F', permesso: 'P', malattia: 'M' }[a.type] || '';
      let mins = 0;
      let title = `${TYPE_STYLE[a.type]?.label || a.type}${a.time_from ? ` ${a.time_from}-${a.time_to}` : ''}`;
      if (a.type === 'permesso' && a.time_from && a.time_to) {
        // permesso parziale: nel totale contano le ore comunque lavorate (es. 4 su 8)
        const [h1, m1] = a.time_from.split(':').map(Number);
        const [h2, m2] = a.time_to.split(':').map(Number);
        const permMin = Math.max(0, (h2 * 60 + m2) - (h1 * 60 + m1));
        mins = worked || Math.max(0, scheduledMinutes(e, wd) - permMin);
        if (mins) title += ` · contano ${fmtHours(mins)} ore lavorate`;
      }
      return { cls: `${TYPE_STYLE[a.type]?.cell || 'bg-slate-400'} text-white font-bold`, content: letter, title, mins, iso, editable: false };
    }
    const mins = worked || scheduledMinutes(e, wd);
    return {
      cls: worked ? 'bg-white text-slate-800 font-semibold' : 'bg-white text-slate-500',
      content: mins ? fmtHours(mins) : '',
      title: mins ? `${fmtHours(mins)} ore ${manual ? 'inserite manualmente' : (worked ? 'timbrate' : 'previste')} — clicca per modificare` : 'clicca per inserire le ore',
      mins,
      iso,
      editable: true,
      hasData: !!worked,
    };
  };

  const rowsData = activeEmps.map(e => {
    const cells = days.map(d => cellInfo(e, d));
    return { emp: e, cells, totalMin: cells.reduce((s, c) => s + c.mins, 0) };
  });
  const dayTotals = days.map((_, i) => rowsData.reduce((s, r) => s + r.cells[i].mins, 0));
  const grandTotal = dayTotals.reduce((s, v) => s + v, 0);

  const shiftMonth = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(d.toLocaleDateString('sv-SE').slice(0, 7));
  };

  return (
    <div className="space-y-4 mt-4" data-testid="hr-absences-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="hr-cal-prev" onClick={() => shiftMonth(-1)}>←</Button>
          <span className="font-semibold text-slate-800 w-36 text-center" data-testid="hr-cal-month">
            {new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
          </span>
          <Button variant="outline" size="sm" data-testid="hr-cal-next" onClick={() => shiftMonth(1)}>→</Button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 flex gap-2">
            {Object.values(TYPE_STYLE).map(s => <span key={s.label}><span className={`inline-block w-3 h-3 rounded ${s.cell} align-middle mr-1`} />{s.label.slice(2)}</span>)}
          </span>
          <Button variant="outline" size="sm" data-testid="hr-fill-month-btn" onClick={async () => {
            if (!window.confirm(`Compilare i giorni di ${new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })} senza timbrature né assenze come "lavorati da orario" (fino a oggi)? Potrai poi correggere i singoli giorni cliccando sulle celle.`)) return;
            try {
              const r = await axios.post(`${API}/hr/fill-month`, { month }, { headers: authHeaders() });
              const sk = r.data.skipped || {};
              const parts = [];
              if (sk.gia_presenti) parts.push(`${sk.gia_presenti} già compilati/timbrati`);
              if (sk.assenze) parts.push(`${sk.assenze} in ferie/malattia`);
              if (sk.senza_orario) parts.push(`${sk.senza_orario} senza orario previsto`);
              const detail = parts.length ? ` — saltati: ${parts.join(', ')}` : '';
              if (r.data.filled > 0) toast.success(`${r.data.filled} giorni compilati per ${r.data.employees} dipendenti (${r.data.month})${detail}`);
              else toast.info(`Nessun nuovo giorno da compilare in ${r.data.month} per ${r.data.employees} dipendenti${detail || ' — tutto già a posto'}`, { duration: 9000 });
              load();
            } catch (err) { toast.error(err?.response?.data?.detail || 'Errore compilazione mese'); }
          }}>
            ⚙️ Compila mese da orario
          </Button>
          <Button data-testid="hr-abs-add-btn" size="sm" onClick={() => { setDialog(emptyDialog()); setError(''); }}>
            <Plus className="h-4 w-4 mr-1" /> Nuova assenza
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-300 rounded-xl bg-white">
        <table className="w-full table-fixed text-xs border-collapse" data-testid="hr-abs-calendar">
          <colgroup>
            <col style={{ width: '150px' }} />
            {days.map(d => <col key={d} />)}
            <col style={{ width: '64px' }} />
          </colgroup>
          <thead><tr className="bg-slate-100">
            <th className="text-left py-2 px-3 text-slate-600 border border-slate-300 sticky left-0 bg-slate-100 z-10">Dipendente</th>
            {days.map(d => {
              const wd = new Date(y, m - 1, d).getDay();
              const hol = holidayMap[`${month}-${String(d).padStart(2, '0')}`];
              const we = wd === 0 || wd === 6 || !!hol;
              return (
                <th key={d} title={hol || ''} className={`px-0 py-1 font-normal border border-slate-300 ${we ? 'bg-slate-300 text-slate-700' : 'text-slate-500'}`}>
                  <div className={`text-[9px] font-bold leading-none ${we ? 'text-slate-700' : 'text-slate-400'}`}>{hol ? '★' : 'DLMMGVS'[wd]}</div>
                  <div className="leading-tight font-semibold">{d}</div>
                </th>
              );
            })}
            <th className="px-1 py-1 border border-slate-300 text-slate-700 font-bold bg-slate-200">Totale</th>
          </tr></thead>
          <tbody>
            {rowsData.map(({ emp: e, cells, totalMin }) => (
              <tr key={e.id}>
                <td className="py-1.5 px-3 font-medium text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis border border-slate-300 sticky left-0 bg-white z-10">{e.name}</td>
                {cells.map((c, i) => (
                  <td key={i} title={c.title}
                    onClick={() => { if (c.editable) setDayDialog({ empId: e.id, empName: e.name, iso: c.iso, hours: c.mins ? fmtHours(c.mins).replace(',', '.') : '8', hasData: c.hasData }); }}
                    className={`border border-slate-200 text-center align-middle h-8 text-[11px] ${c.cls} ${c.editable ? 'cursor-pointer hover:bg-blue-50' : ''}`}>
                    {c.content}
                  </td>
                ))}
                <td className="border border-slate-300 text-center font-bold text-slate-800 bg-slate-50" data-testid={`hr-abs-total-${e.id}`}>
                  {fmtHours(totalMin)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100">
              <td className="py-1.5 px-3 font-bold text-slate-700 border border-slate-300 sticky left-0 bg-slate-100 z-10">Totale giorno</td>
              {dayTotals.map((t, i) => (
                <td key={i} className="border border-slate-300 text-center font-semibold text-slate-700 text-[11px]" data-testid={`hr-abs-day-total-${i + 1}`}>
                  {t ? fmtHours(t) : ''}
                </td>
              ))}
              <td className="border border-slate-300 text-center font-bold text-slate-900 bg-slate-200" data-testid="hr-abs-grand-total">
                {fmtHours(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="space-y-2" data-testid="hr-abs-list">
        <p className="text-sm font-semibold text-slate-700">Assenze del mese</p>
        {absences.length === 0 && <p className="text-sm text-slate-400" data-testid="hr-abs-list-empty">Nessuna assenza nel mese</p>}
        {absences.map(a => (
          <div key={a.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 text-sm">
            <span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold mr-2 ${TYPE_STYLE[a.type]?.badge || 'bg-slate-100'}`}>{TYPE_STYLE[a.type]?.label || a.type}</span>
              <b>{a.employee_name}</b> · {fmtD(a.date_from)}{a.date_to !== a.date_from ? `–${fmtD(a.date_to)}` : ''}
              {a.time_from ? ` ${a.time_from}-${a.time_to}` : ''}
              <span className="text-slate-400 ml-1">{a.days_count ? `(${a.days_count} gg)` : a.hours_count ? `(${a.hours_count} h)` : ''}{a.note ? ` — ${a.note}` : ''}</span>
              {a.type === 'malattia' && (a.certificates || []).length > 0 && <span className="text-sky-600 text-xs ml-1 font-semibold">📎{a.certificates.length}</span>}
              {a.type === 'malattia' && a.inps_protocol && <span className="text-slate-500 text-xs ml-1">INPS {a.inps_protocol}</span>}
            </span>
            <span className="flex items-center gap-1">
              {a.type === 'malattia' && (
                <Button variant="ghost" size="sm" title="Certificato medico" data-testid={`hr-cert-admin-btn-${a.id}`}
                  onClick={() => { setCertDialog(a); setCertFiles([]); setCertProt(a.inps_protocol || ''); }}>
                  <Paperclip className="h-4 w-4 text-sky-600" />
                </Button>
              )}
              <Button variant="ghost" size="sm" data-testid={`hr-abs-admin-cancel-${a.id}`} onClick={() => cancel(a)}>
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            </span>
          </div>
        ))}
      </div>

      {dialog && (
        <Dialog open onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="max-w-md" data-testid="hr-abs-admin-dialog">
            <DialogHeader><DialogTitle>Nuova assenza</DialogTitle>
              <DialogDescription>Registra ferie, permesso o malattia per un dipendente (saldi scalati subito)</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Dipendente</Label>
                <select data-testid="hr-abs-admin-emp" className="w-full border rounded-md h-9 px-2 text-sm" value={dialog.employee_id} onChange={e => setDialog({ ...dialog, employee_id: e.target.value })}>
                  <option value="">— seleziona —</option>
                  {activeEmps.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Tipo</Label>
                <select data-testid="hr-abs-admin-type" className="w-full border rounded-md h-9 px-2 text-sm" value={dialog.type} onChange={e => setDialog({ ...dialog, type: e.target.value })}>
                  <option value="ferie">🌴 Ferie</option>
                  <option value="permesso">🕐 Permesso orario</option>
                  <option value="malattia">🤒 Malattia</option>
                </select>
              </div>
              {dialog.type === 'permesso' ? (
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs">Giorno</Label><Input type="date" data-testid="hr-abs-admin-from" value={dialog.date_from} onChange={e => setDialog({ ...dialog, date_from: e.target.value })} /></div>
                  <div><Label className="text-xs">Dalle</Label><Input type="time" data-testid="hr-abs-admin-tfrom" value={dialog.time_from} onChange={e => setDialog({ ...dialog, time_from: e.target.value })} /></div>
                  <div><Label className="text-xs">Alle</Label><Input type="time" data-testid="hr-abs-admin-tto" value={dialog.time_to} onChange={e => setDialog({ ...dialog, time_to: e.target.value })} /></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Dal</Label><Input type="date" data-testid="hr-abs-admin-from" value={dialog.date_from} onChange={e => setDialog({ ...dialog, date_from: e.target.value })} /></div>
                  <div><Label className="text-xs">Al</Label><Input type="date" data-testid="hr-abs-admin-to" value={dialog.date_to} onChange={e => setDialog({ ...dialog, date_to: e.target.value })} /></div>
                </div>
              )}
              <div><Label className="text-xs">Nota</Label><Input data-testid="hr-abs-admin-note" value={dialog.note} onChange={e => setDialog({ ...dialog, note: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" data-testid="hr-abs-admin-force" checked={dialog.force} onChange={e => setDialog({ ...dialog, force: e.target.checked })} />
                Forza (ignora la regola dei 2 presenti su 4 del customer service)
              </label>
              {error && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="hr-abs-admin-error">{error}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setDialog(null)}>Annulla</Button>
                <Button data-testid="hr-abs-admin-save" onClick={save} disabled={saving}>{saving ? 'Salvataggio…' : 'Registra'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {certDialog && (
        <Dialog open onOpenChange={v => !v && setCertDialog(null)}>
          <DialogContent className="max-w-md" data-testid="hr-cert-admin-dialog">
            <DialogHeader>
              <DialogTitle>📎 Certificato medico — {certDialog.employee_name}</DialogTitle>
              <DialogDescription>
                Malattia {fmtD(certDialog.date_from)}{certDialog.date_to !== certDialog.date_from ? `–${fmtD(certDialog.date_to)}` : ''} ·
                al caricamento parte l'email automatica a segreteria@cedelabora.it + amministrazione
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {(certDialog.certificates || []).length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">File caricati</Label>
                  {certDialog.certificates.map(c => (
                    <div key={c.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`hr-cert-file-row-${c.id}`}>
                      <button className="flex items-center gap-1.5 text-sky-700 hover:underline text-left" data-testid={`hr-cert-admin-download-${c.id}`}
                        onClick={() => openCertificate(certDialog.id, c.id).catch(() => toast.error('Errore download'))}>
                        <Download className="h-3.5 w-3.5" /> {c.file_name}
                      </button>
                      <button className="text-rose-500 hover:text-rose-600" title="Elimina file" data-testid={`hr-cert-admin-delete-${c.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Eliminare il file "${c.file_name}"?`)) return;
                          try {
                            await axios.delete(`${API}/hr/absences/${certDialog.id}/certificate/${c.id}`, { headers: authHeaders() });
                            toast.success('File eliminato');
                            setCertDialog({ ...certDialog, certificates: certDialog.certificates.filter(x => x.id !== c.id) });
                            load();
                          } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
                        }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label className="text-xs">Aggiungi file (qualsiasi formato, anche più pagine/foto)</Label>
                <input type="file" multiple data-testid="hr-cert-admin-files" className="w-full text-sm mt-1"
                  onChange={e => setCertFiles(Array.from(e.target.files || []))} />
              </div>
              <div>
                <Label className="text-xs">N. protocollo INPS (facoltativo)</Label>
                <Input data-testid="hr-cert-admin-inps" value={certProt} onChange={e => setCertProt(e.target.value)} placeholder="es. 123456789" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setCertDialog(null)}>Chiudi</Button>
                <Button data-testid="hr-cert-admin-save" disabled={certBusy} onClick={async () => {
                  if (!certFiles.length && !certProt.trim()) { toast.error('Allega un file o indica il protocollo INPS'); return; }
                  setCertBusy(true);
                  try {
                    const r = await uploadCertificate(certDialog.id, certFiles, certProt.trim());
                    toast.success(r.email_sent ? 'Certificato salvato e inviato alla segreteria ✅' : 'Certificato salvato ✅ (email non inviata)');
                    setCertDialog(null); load();
                  } catch (err) { toast.error(err?.response?.data?.detail || 'Errore caricamento'); }
                  finally { setCertBusy(false); }
                }}>{certBusy ? 'Caricamento…' : 'Carica e invia'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {dayDialog && (
        <Dialog open onOpenChange={() => setDayDialog(null)}>
          <DialogContent className="max-w-sm" data-testid="hr-day-dialog">
            <DialogHeader>
              <DialogTitle>Ore lavorate — {dayDialog.empName}</DialogTitle>
              <DialogDescription>{new Date(dayDialog.iso + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Ore lavorate nel giorno</Label>
                <Input type="number" step="0.5" min="0" max="16" data-testid="hr-day-hours"
                  value={dayDialog.hours}
                  onChange={ev => setDayDialog({ ...dayDialog, hours: ev.target.value })} />
              </div>
              <div className="flex justify-between gap-2">
                {dayDialog.hasData ? (
                  <Button variant="outline" className="text-rose-600" data-testid="hr-day-remove" disabled={savingDay} onClick={async () => {
                    setSavingDay(true);
                    try {
                      await axios.post(`${API}/hr/manual-day`, { employee_id: dayDialog.empId, day: dayDialog.iso, hours: null }, { headers: authHeaders() });
                      toast.success('Dato rimosso');
                      setDayDialog(null); load();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
                    setSavingDay(false);
                  }}><Trash2 className="h-4 w-4 mr-1" /> Rimuovi</Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setDayDialog(null)}>Annulla</Button>
                  <Button data-testid="hr-day-save" disabled={savingDay} onClick={async () => {
                    setSavingDay(true);
                    try {
                      await axios.post(`${API}/hr/manual-day`, { employee_id: dayDialog.empId, day: dayDialog.iso, hours: dayDialog.hours }, { headers: authHeaders() });
                      toast.success('Ore salvate');
                      setDayDialog(null); load();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
                    setSavingDay(false);
                  }}>{savingDay ? 'Salvataggio…' : 'Salva'}</Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
